import { FC, useContext } from "react";
import ShowPatientInformation from "../addCase/showPatientInformation";
import ShowPatientAddress from "../addCase/showPatientAddress";
import ShowPrescriberInformation from "../addCase/showPrescriberInformation";
import ShowInsuranceInformation from "../addCase/showInsuranceInformation";
import { CaseStepThreeProps } from "./types";
import { Controller } from "react-hook-form";
import { AuthContext } from "src/context/authContext";

const CaseStepThree: FC<CaseStepThreeProps> = ({ control, getValues }): JSX.Element => {
  const { new_baa_user_1_2 } = useContext(AuthContext)
  const { insurances_attributes, prescription_attributes } = getValues();

  return (
    <>
      <ShowPatientInformation getValues={getValues} />

      <ShowPatientAddress getValues={getValues} />

      <ShowPrescriberInformation getValues={getValues} />

      {insurances_attributes ? insurances_attributes.map((item, index) => (
        <ShowInsuranceInformation key={index} id={index + 1} insurance={item} />
      )) : null}

      <div className="alert alert-light mt-3 rounded-1" role="alert">
        My QUTENZA Connect assists health care professionals in the determination of
        whether treatment could be covered by the applicable third-party payer based
        on coverage guidelines provided by the payer and patient information provided
        by the health care provider. Third-party reimbursement is affected by many factors.
        Therefore, My QUTENZA Connect makes no representation or guarantee that full or partial
        insurance reimbursement or any other payment will be available.
      </div>

      <Controller
        name="patient_attributes.reinvestigation"
        control={control}
        render={({ field }) => (
          <div className="alert alert-warning mt-3 rounded-1" role="alert">
            <div className="form-check">
              <input
                className="form-check-input" type="checkbox" id="patient_attributes.reinvestigation"
                checked={field.value} onChange={(e) => field.onChange(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="patient_attributes.reinvestigation">
                Automatically re-verify patient benefits for potential retreatment every 91 days
              </label>
            </div>
          </div>
        )}
      />

      {(typeof new_baa_user_1_2 === "boolean" && new_baa_user_1_2 === false) ?
        <>
          <Controller
            name="patient_attributes.pcc_outreach"
            control={control}
            render={({ field }) => (
              <div className="alert alert-warning mt-3 rounded-1" role="alert">
                <div className="form-check">
                  <input
                    className="form-check-input" type="checkbox" id="patient_attributes.pcc_outreach"
                    checked={["Other", "PHN"].includes(prescription_attributes.indication) ? false : field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={["Other", "PHN"].includes(prescription_attributes.indication)}
                  />
                  <label className="form-check-label" htmlFor="patient_attributes.pcc_outreach">
                    Automatically initiate outreach by a Patient Care Coordinator who will contact the patient directly to
                    provide insurance coverage details specific to treatment with QUTENZA three days after the Benefits
                    Investigation is completed by My QUTENZA Connect. Patients will be asked to provide additional consent
                    to proceed with the program and may opt-out at any time.
                  </label>
                </div>
              </div>
            )}
          />
          <Controller
            name="patient_attributes.outreach"
            control={control}
            render={({ field }) => (
              <div className="alert alert-warning mt-3 rounded-1" role="alert">
                <div className="form-check">
                  <input
                    className="form-check-input" type="checkbox" id="patient_attributes.outreach"
                    checked={["Other", "PHN"].includes(prescription_attributes.indication) ? false : field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={["Other", "PHN"].includes(prescription_attributes.indication)}
                  />
                  <label className="form-check-label" htmlFor="patient_attributes.outreach">
                    Automatically initiate outreach by a QUTENZA Nurse Specialist who will contact the patient directly to provide 
                    education and support specific to treatment with QUTENZA. Patients will be asked to provide additional consent 
                    to proceed with the program and may opt-out at any time.
                  </label>
                </div>
              </div>
            )}
          />
        </>
      : null}

      <div className="alert alert-warning rounded-1" role="alert">
        By submitting this form, I certify 1) I am duly licensed and authorized under applicable law to prescribe,
        receive and dispense the medication requested in this application to the patient listed above (the 'Patient');
        2) The information provided above is complete and accurate; 3) I authorize My QUTENZA Connect to forward the
        above prescription information to the appropriate pharmacy in order to dispense 'QUTENZA' to the above patient's
        provider; 4) I understand that state law may require the pharmacy to contact me to confirm the prescription
        information before dispensing; 5) By signing this form, I represent to My QUTENZA Connect that I have obtained
        all necessary Federal and state authorizations and consents from my patient to allow me to release health
        information to My QUTENZA Connect and its contracted third parties, including those that may contact the
        patient directly for patient support services. Signature on this form also provides consent to contact this
        patient's insurance provider for this prescription on the prescriber's behalf.
      </div>

      {prescription_attributes.indication === "Other" && 
        <div className="alert alert-warning rounded-1" role="alert">
          <b>
            PLEASE NOTE: MQC cannot support the prior authorization process for indications other than DPN of 
            the feet or PHN
          </b>
        </div>
      }
    </>
  );
};

export default CaseStepThree;
